import { Injectable } from "@angular/core";
import * as _ from "lodash";
import { ApiService } from "../../services";
import { AccessRequestServiceType, RequestServiceType, allowedCommonRegionStatuses } from "../../npr-request.model";
import { Region, Team, VmSku, EdgeZone, AZ } from "../../generated-models";
import { map } from "rxjs/operators";

@Injectable()
export class DataTypes {
  private options: { [key: string]: (string | { label: string; value: string })[] } = {};
  // this map contains both oid-> name and name-> oid
  teamNameOidMap: { [key: string]: string } = {};
  regionMap: { [key: string]: string } = {};
  skuMap: { [key: string]: string } = {};
  azRegionMap: { [key: string]: string } = {};
  serviceTypeMap: { [key: string]: string } = {};
  blueprintConfigMap: { [serviceTreeId: string]: { label: string; value: string; serviceTreeId: string }[] } = {};
  
  constructor(private apiService: ApiService) {
    this.regions();
    this.edgezones();
    this.blueprints();
    this.azs();
    
    // Initialize service type friendly name mapping first
    this.initServiceTypeMap();
    
    this.options.serviceType = Object.keys(RequestServiceType);
    this.options.accessServiceType = Object.keys(AccessRequestServiceType);
    this.options.channel = ["Lionrock", "CIS"];
    this.options.serviceHandling = ["NotAvailable", "Manual", "Automatable", "ForceAutomation", "Disabled"];
    this.options.approvalType = ["Ag", "GCT", "HDInsight", "Batch", "DNS", "CosmosDB"];
    this.options.internal = ["Internal", "External"];
    this.options.requestSource = ["EV2", "LionrockUI", "CIS"];
    this.options.regionType = ["Hero", "Hub", "Satellite", "Micro"];
    this.options.hoboAutoApprovalSupportedServiceTypes = ["ArmVmQuota", "ArmStorageQuota"];
    this.options.skipAutoApproval = ["None", "All", "Quota", "Access"];
    
    // Options for AutoApprovalThreshold - display key, store value
    this.options.executionPlanServiceType = Object.keys(this.serviceTypeMap).map((key) => {
      return { label: key, value: this.serviceTypeMap[key] };
    });
  }

  private initServiceTypeMap(): void {
    const serviceTypeMapping: { [key: string]: string } = {
      "ArmVmQuota": "VM",
      "ArmStorageQuota": "STORAGE",
      "SQL": "SQL",
      "SQLMI": "SQLMI",
      "DNSEntry": "DNS",
      "Batch": "BATCH",
      "HDInsight": "HDINSIGHT",
      "CosmosDB": "COSMOSDB",
      "AppService": "APPSERVICE",
      "VmDisk": "VMDISK",
      "EnableRegion": "ENABLEREGION",
      "EnableAZ": "ENABLEAZ",
      "EnableResourceType": "ENABLEARMRESOURCETYPE"
    };

    Object.keys(serviceTypeMapping).forEach(key => {
      this.serviceTypeMap[key] = serviceTypeMapping[key];
    });
  }

  initTeamRegion(teams: Team[], regions: Region[]): void {
    this.options.Teams = [];
    _.forEach(teams, team => {
      this.options.Teams.push({ label: `${team.Name}(${team.Oid})`, value: team.Oid });
      this.teamNameOidMap[team.Oid] = team.Name;
    });
    _.forEach(regions, region => {
      this.regionMap[region.RegionName] = region.RegionName;
    });
    this.regionMap["*"] = "*";
    this.options.requestor = teams.map((team) => {
      return { label: team.Name, value: team.Oid };
    });
    this.teamNameOidMap["*"] = "*";
    this.options.requestor["*"] = "*";
    if (this.options.region && this.options.region[0] !== "*") {
      this.options.region.unshift("*");
    }

    // Initialize location options (RegionName or CloudName from regions)
    // Create flat array with group property for ng-select groupBy
    const locationOptions: { label: string; value: string; group: string }[] = [];
    
    const regionLocations: string[] = [];
    const cloudLocations: string[] = [];
    
    _.forEach(regions, region => {
      // Add RegionName
      if (region.RegionName) {
        regionLocations.push(region.RegionName);
      }
      
      // Add CloudName if it exists and is different from RegionName
      if (region.CloudName && region.CloudName !== region.RegionName) {
        cloudLocations.push(region.CloudName);
      }
    });

    // Create flat array with group property for region locations
    if (regionLocations.length > 0) {
      regionLocations
        .filter((location, index, self) => self.indexOf(location) === index) // Remove duplicates
        .sort()
        .forEach(location => {
          locationOptions.push({
            label: location,
            value: location,
            group: 'Region'
          });
        });
    }
    
    // Create flat array with group property for cloud locations
    if (cloudLocations.length > 0) {
      cloudLocations
        .filter((location, index, self) => self.indexOf(location) === index) // Remove duplicates
        .sort()
        .forEach(location => {
          locationOptions.push({
            label: location,
            value: location,
            group: 'Cloud'
          });
        });
    }
    
    this.options.location = locationOptions;
  }

  initVmSku(vmsku: VmSku[]): void {
    this.options.sku = vmsku.map((sku) => {
      return { label: sku.PortalVmSku, value: sku.CrpVmSku };
    });
    vmsku.map((sku) => {
      this.skuMap[sku.CrpVmSku] = sku.PortalVmSku;
    });
  }

  regions(): void {
    this.apiService.getRegionList()
      .pipe(map(regions => regions.filter(region => allowedCommonRegionStatuses.includes(region.Status))))
      .subscribe((regions: Region[]) => (this.options.region = regions.map((r) => r.RegionName)));
  }

  edgezones(): void {
    this.apiService.getEdgeZoneList().subscribe((edgezones: EdgeZone[]) => (this.options.edgezone = edgezones.map((r) => r.EdgeZoneId)));
  }

  azs(): void {
    this.apiService.getAzEntity(true).subscribe((azs: AZ[]) => {
      this.options.az = azs.map((a) => a.AvailabilityZoneName);
      azs.forEach((a) => (this.azRegionMap[a.AvailabilityZoneName] = a.RegionName));
    });
  }

  blueprints(): void {
    this.apiService.getPlans().subscribe((plans) => {
      this.options.blueprint = plans
        .filter((p) => !p.IsMigrated)
        .filter((p) => p.Version > 0)
        .map((p) => {
          return { label: `${p.Name} - ${p.ServiceTeam} (${p.ServiceTreeId})`, value: p.Name, serviceTreeId: p.ServiceTreeId };
        });
      
      (this.options.blueprint as { label: string; value: string; serviceTreeId: string }[]).forEach((blueprint) => {
        (this.blueprintConfigMap[blueprint.serviceTreeId] ??= []).push(blueprint);
      });
    });
  }

  getOptions(type: string): (string | { label: string; value: string })[] {
    return this.options[type] || null;
  }
}
